import {
  BasketLink,
  FeedbackForm,
  FeedbackFormVariant,
  ImpactShopHero,
  ImpactShopHomeContent,
  TopNavigation,
} from "@ecologi/react-components";
import {
  ImpactShopProductType,
  locationCurrencyCodeMapper,
} from "@ecologi/shared";
import {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import { Head } from "../../components";
import { auth0, pushImpactShopProductClick } from "../../lib";
import { useAnalytics } from "../../lib/analytics/useAnalytics";
import { createGeneralFeedback } from "../../lib/api/general-feedback";
import { getAllImpactShopProducts } from "../../lib/api/impact-shop";
import { withLogging } from "../../lib/ssr/logGetServerSideProps";
import { useStore } from "../../store";
import { useFetchImpactShopStore } from "../../store/impact-shop";

export const IMPACT_SHOP_UNAUTHENTICATED_REDIRECT = "/business/impact-shop";

const useImpactShopStore = useFetchImpactShopStore();

const ImpactShopPageComponent = ({
  products,
}: {
  products: ImpactShopProductType[];
}) => {
  const { location, user } = useStore();
  const { asPath } = useRouter();
  const { totalNumberOfProductsInBasket, setCurrency, currency } =
    useImpactShopStore();
  const [basketQty, setBasketQty] = useState(0);
  const { sendAnalyticsEvent } = useAnalytics();

  useEffect(() => {
    const currency =
      user?.preferredCurrency ||
      locationCurrencyCodeMapper[location.countryCode];

    setCurrency(currency);
  }, [user?.preferredCurrency, location]);

  useIsomorphicLayoutEffect(() => {
    setBasketQty(totalNumberOfProductsInBasket());
  }, [totalNumberOfProductsInBasket]);

  const handleProductClick = (slug: string) => {
    pushImpactShopProductClick({ slug });
    sendAnalyticsEvent("impactShopProductClick", {
      productSlug: slug,
    });
  };

  const handleFilterClick = (filterName: string) => {
    sendAnalyticsEvent("impactShopFilterOptionClick", {
      impactShopFilterName: filterName,
    });
  };

  return (
    <>
      <Head title="Impact shop" />
      <TopNavigation
        loggedOutNav={!user}
        rightSlot={
          <BasketLink href="/impact-shop/basket" itemQuantity={basketQty} />
        }
      />
      <ImpactShopHero />
      <ImpactShopHomeContent
        products={products}
        currency={currency}
        onProductClick={handleProductClick}
        onFilterClick={handleFilterClick}
      />
      {user && (
        <FeedbackForm
          onSubmit={createGeneralFeedback}
          currentUser={user}
          currentPath={asPath}
          variant={FeedbackFormVariant.SNOWDROP}
        />
      )}
    </>
  );
};

export const getServerSideProps = withLogging(
  async (context: GetServerSidePropsContext) => {
    const tokenCache = auth0.tokenCache(
      context.req as NextApiRequest,
      context.res as NextApiResponse
    );

    const session = await auth0.getSession(context.req);

    const products = await getAllImpactShopProducts(
      session ? tokenCache : undefined
    );

    return {
      props: {
        layoutProps: {
          hideHeader: true,
          isWhiteBody: true,
          isProfile: true,
        },
        products,
      },
    };
  }
);

export default ImpactShopPageComponent;
